<template>
  <div class="tax-config pa-2 w-100">
    <Typography variant="h-4" class="mb-2">Tax Config</Typography>
    <Typography variant="body-2" class="mb-6">
      These credentials connect PerformX to WIR (identity verification) and Data Vault (encrypted
      storage), so we can securely collect and store tax info submitted by your users.
    </Typography>

    <v-row>
      <v-col cols="12" md="6">
        <Typography variant="subtitle-1" class="mb-3">WIR API Credentials</Typography>
        <v-text-field
          v-for="field in wirFields"
          :key="field.key"
          v-model="values[field.key]"
          :ref="field.key"
          :label="field.label"
          :type="fieldType(field)"
          :readonly="locked[field.key]"
          :rules="fieldRules[field.key]"
          :placeholder="locked[field.key] ? savedPlaceholder : undefined"
          :append-icon="locked[field.key] ? 'mdi-pencil' : undefined"
          :class="`tax-config-field tax-config-field--${field.key}`"
          :loading="loadingStatus"
          persistent-placeholder
          autocomplete="new-password"
          outlined
          dense
          hide-details="auto"
          class="mb-4"
          @click:append="startEditing(field.key)"
        />
      </v-col>
      <v-col cols="12" md="6">
        <Typography variant="subtitle-1" class="mb-3">Data Vault Credentials</Typography>
        <v-text-field
          v-for="field in dataVaultFields"
          :key="field.key"
          v-model="values[field.key]"
          :ref="field.key"
          :label="field.label"
          :type="fieldType(field)"
          :readonly="locked[field.key]"
          :rules="fieldRules[field.key]"
          :placeholder="locked[field.key] ? savedPlaceholder : undefined"
          :append-icon="locked[field.key] ? 'mdi-pencil' : undefined"
          :class="`tax-config-field tax-config-field--${field.key}`"
          :loading="loadingStatus"
          persistent-placeholder
          autocomplete="new-password"
          outlined
          dense
          hide-details="auto"
          class="mb-4"
          @click:append="startEditing(field.key)"
        />
      </v-col>
    </v-row>

    <div class="d-flex align-center flex-wrap">
      <Button
        outlined
        color="brandPrimary"
        icon-left="mdi-play-circle-outline"
        :disabled="!canTestConnection"
        :loading="testing"
        @click="handleTestConnection"
      >
        Test connection
      </Button>
      <Alert
        v-if="testPassed"
        variant="success"
        message="Connection tested successfully!"
        class="ml-3 tax-config-test-all"
      />
    </div>

    <div v-if="testResult && !testPassed" class="mt-4">
      <Alert
        v-for="check in testChecks"
        :key="check.key"
        :variant="check.success ? 'success' : 'error'"
        :message="check.message"
        :class="`mb-2 tax-config-test-${check.key}`"
      />
    </div>

    <v-divider class="my-6" />

    <div class="d-flex align-start mb-4">
      <v-switch
        v-model="enableTaxInfo"
        :disabled="!canEnableTaxInfo && !enableTaxInfo"
        color="brandPrimary"
        class="mt-2 pt-0 mr-3 tax-config-toggle"
        hide-details
        inset
      />
      <div>
        <Typography variant="subtitle-1">Enable Tax Info</Typography>
        <Typography variant="body-2" class="grey--text text--darken-1">
          Complete all four fields above and test the connection to enable.
        </Typography>
      </div>
    </div>

    <Alert
      variant="warning"
      class="mb-6"
      message="Turning this on immediately shows tax info fields in Edit person and My account → Profile, and starts prompting users in Wallet. This affects every user on this account."
    />

    <div class="d-flex justify-center">
      <Button color="brandPrimary" :disabled="!canSave" :loading="saving" @click="handleSave">
        Save Changes
      </Button>
    </div>

    <ErrorDialogV2
      v-model="confirmDialog.show"
      :title="confirmDialog.title"
      :message="confirmDialog.message"
      :confirm-button-text="confirmDialog.confirmButtonText"
      :cancel-button-text="confirmDialog.cancelButtonText"
      :show-cancel-button="true"
      @confirm="onConfirmDialogConfirm"
      @cancel="onConfirmDialogCancel"
    />
  </div>
</template>

<script>
import Typography from "@/components/generic/Typography.vue";
import Button from "@/components/generic/Button.vue";
import Alert from "@/components/generic/Alert.vue";
import ErrorDialogV2 from "@/components/ErrorDialogV2.vue";
import ClientServiceV2 from "@/services/ClientServiceV2";
import UserServiceV3 from "@/services/UserServiceV3";

const WIR_FIELDS = [
  { key: "wirClientSecret", label: "WIR API Client Secret", masked: true },
  { key: "wirSubscriptionKey", label: "WIR API Subscription Key", masked: true },
];

// WIR and Data Vault are separate upstream tenants but share one id here, so this
// single field supplies `tenantId` on both save requests.
const DATA_VAULT_FIELDS = [
  { key: "dataVaultSubscriptionKey", label: "Data Vault Subscription Key", masked: true },
  { key: "tenantId", label: "WIR / Data Vault Tenant ID", masked: false },
];

const SECRET_KEYS = new Set(["wirClientSecret", "wirSubscriptionKey", "dataVaultSubscriptionKey"]);
const FIELD_KEYS = [...WIR_FIELDS, ...DATA_VAULT_FIELDS].map((field) => field.key);

// Each group owns an endpoint pair: POST creates the whole bundle, PATCH updates just the
// fields supplied. Values map form keys to the request property each endpoint expects.
const GROUP_SECRET_FIELDS = {
  wir: { wirClientSecret: "clientSecret", wirSubscriptionKey: "subscriptionKey" },
  dataVault: { dataVaultSubscriptionKey: "subscriptionKey" },
};
const GROUP_KEYS = Object.keys(GROUP_SECRET_FIELDS);

const MAX_LENGTH = 255;

// Values are only trimmed on save, so the rule trims before measuring to keep stray
// whitespace from a pasted credential out of the error message.
const lengthRule = (v) =>
  !v || v.trim().length < MAX_LENGTH || `This should be shorter than ${MAX_LENGTH} characters`;

const FIELD_RULES = FIELD_KEYS.reduce((acc, key) => ({ ...acc, [key]: [lengthRule] }), {});

const byKey = (value) => FIELD_KEYS.reduce((acc, key) => ({ ...acc, [key]: value }), {});

export default {
  name: "TaxConfig",
  components: {
    Typography,
    Button,
    Alert,
    ErrorDialogV2,
  },
  props: {
    client: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      savedPlaceholder: "••••••••••••",
      clientService: new ClientServiceV2(),
      wirFields: WIR_FIELDS,
      dataVaultFields: DATA_VAULT_FIELDS,
      fieldRules: FIELD_RULES,
      values: byKey(""),
      locked: byKey(false),
      savedTenantId: "",
      wirConfigured: false,
      dataVaultConfigured: false,
      loadingStatus: false,
      enableTaxInfo: false,
      savedEnableTaxInfo: false,
      saving: false,
      testing: false,
      testResult: null,
      confirmDialog: {
        show: false,
        title: "",
        message: "",
        confirmButtonText: "Confirm",
        cancelButtonText: "Cancel",
      },
      confirmDialogResolve: null,
    };
  },
  computed: {
    credentialsConfigured() {
      return this.wirConfigured && this.dataVaultConfigured;
    },
    tenantId() {
      return (this.values.tenantId || "").trim();
    },
    /** WIR and Data Vault share one tenant id, so a group being saved can reuse the stored one. */
    effectiveTenantId() {
      return this.tenantId || this.savedTenantId;
    },
    tenantIdChanged() {
      return !!this.tenantId && this.tenantId !== this.savedTenantId;
    },
    /** Groups needing a request, judged on their own fields plus the shared tenant id. */
    dirtyGroups() {
      return GROUP_KEYS.filter((group) => this.isGroupDirty(group));
    },
    /** Both sides have credentials, either already stored or fully entered here. */
    credentialsReady() {
      return GROUP_KEYS.every(
        (group) => this.isGroupConfigured(group) || this.isGroupComplete(group)
      );
    },
    canEnableTaxInfo() {
      return this.credentialsReady && this.testPassed;
    },
    toggleChanged() {
      return this.enableTaxInfo !== this.savedEnableTaxInfo;
    },
    hasUnsavedChanges() {
      return this.toggleChanged || this.dirtyGroups.length > 0;
    },
    valuesValid() {
      return FIELD_KEYS.every((key) =>
        FIELD_RULES[key].every((rule) => rule(this.values[key]) === true)
      );
    },
    canTestConnection() {
      return !!this.client?.clientId && !this.testing && !this.saving && this.valuesValid;
    },
    /** Only rendered on failure; a full pass is reported inline next to the button. */
    testChecks() {
      if (!this.testResult) return [];
      return [
        { key: "wir", label: "WIR", ...this.testResult.wir },
        { key: "dataVault", label: "Data Vault", ...this.testResult.dataVault },
      ].map((check) => ({
        ...check,
        message:
          check.message || `${check.label} connection ${check.success ? "succeeded" : "failed"}.`,
      }));
    },
    testPassed() {
      return !!(this.testResult?.wir?.success && this.testResult?.dataVault?.success);
    },
    canSave() {
      if (this.saving || !this.valuesValid) return false;
      if (this.dirtyGroups.length) {
        // Credentials are only written once they've been proven to connect.
        if (!this.testPassed) return false;
        return this.dirtyGroups.every((group) => this.canSaveGroup(group));
      }
      return this.toggleChanged;
    },
  },
  watch: {
    values: {
      deep: true,
      handler() {
        // Any edit invalidates the previous result so Save can't use a stale pass.
        this.testResult = null;
      },
    },
    client: {
      immediate: true,
      handler(newClient) {
        this.enableTaxInfo = !!newClient?.taxInfoEnabled;
        this.savedEnableTaxInfo = this.enableTaxInfo;
        this.loadConfigStatus();
      },
    },
    credentialsReady(ready) {
      // Mid-load the flags still describe the previous client, so wait for the real answer.
      if (!ready && !this.loadingStatus) this.enableTaxInfo = false;
    },
    "confirmDialog.show"(newVal) {
      // Dismissing the modal without picking an option counts as "stay".
      if (!newVal && this.confirmDialogResolve) {
        this.confirmDialogResolve(false);
        this.confirmDialogResolve = null;
      }
    },
  },
  methods: {
    /** Credentials stay legible while being typed and are only masked once saved. */
    fieldType(field) {
      return field.masked && this.locked[field.key] ? "password" : "text";
    },
    isGroupConfigured(group) {
      return group === "wir" ? this.wirConfigured : this.dataVaultConfigured;
    },
    hasEnteredSecret(group) {
      return Object.keys(GROUP_SECRET_FIELDS[group]).some(
        (key) => !this.locked[key] && !!(this.values[key] || "").trim()
      );
    },
    isGroupDirty(group) {
      if (this.hasEnteredSecret(group)) return true;
      // A tenant id change only needs sending to groups that already have a record.
      return this.tenantIdChanged && this.isGroupConfigured(group);
    },
    isGroupComplete(group) {
      return (
        !!this.effectiveTenantId &&
        Object.keys(GROUP_SECRET_FIELDS[group]).every((key) => !!(this.values[key] || "").trim())
      );
    },
    /** PATCH body for a configured group - only the fields the admin actually changed. */
    groupPatch(group) {
      const patch = {};
      Object.entries(GROUP_SECRET_FIELDS[group]).forEach(([key, field]) => {
        const value = (this.values[key] || "").trim();
        if (!this.locked[key] && value) patch[field] = value;
      });
      if (this.tenantIdChanged) patch.tenantId = this.tenantId;
      return patch;
    },
    canSaveGroup(group) {
      // An existing record takes any subset; a new one needs the full bundle POST requires.
      if (this.isGroupConfigured(group)) return Object.keys(this.groupPatch(group)).length > 0;
      return this.isGroupComplete(group);
    },
    /** Drops the previous client's state so switching clients can't carry credentials over. */
    resetConfigState() {
      this.wirConfigured = false;
      this.dataVaultConfigured = false;
      this.savedTenantId = "";
      this.testResult = null;
      FIELD_KEYS.forEach((key) => {
        this.$set(this.values, key, "");
        this.$set(this.locked, key, false);
      });
    },
    async loadConfigStatus() {
      const clientId = this.client?.clientId;
      this.resetConfigState();
      if (!clientId) return;

      try {
        this.loadingStatus = true;
        const [wir, dataVault] = await Promise.all([
          UserServiceV3.getWirConfigStatus(clientId),
          UserServiceV3.getDataVaultConfigStatus(clientId),
        ]);

        this.wirConfigured = !!wir?.result?.configured;
        this.dataVaultConfigured = !!dataVault?.result?.configured;

        // Secrets are vaulted and unreadable, so a saved one shows as a locked placeholder.
        SECRET_KEYS.forEach((key) => {
          const configured =
            key === "dataVaultSubscriptionKey" ? this.dataVaultConfigured : this.wirConfigured;
          this.$set(this.values, key, "");
          this.$set(this.locked, key, configured);
        });

        const tenantId = dataVault?.result?.tenantId || wir?.result?.tenantId || "";
        this.savedTenantId = tenantId;
        this.$set(this.values, "tenantId", tenantId);
        this.$set(this.locked, "tenantId", !!tenantId);

        // Now the status is known, a client with nothing stored can't claim to be enabled.
        if (!this.credentialsReady) this.enableTaxInfo = false;
      } catch (error) {
        console.error("Failed to load tax config status", error);
        this.$store.dispatch("showError", {
          message: "Failed to load the saved tax config. Please try again later.",
        });
      } finally {
        this.loadingStatus = false;
      }
    },
    /** Secrets can't be read back so editing one starts empty; the tenant id is plain text. */
    startEditing(key) {
      if (SECRET_KEYS.has(key)) this.$set(this.values, key, "");
      this.$set(this.locked, key, false);
      this.$nextTick(() => {
        const input = this.$refs[key];
        const field = Array.isArray(input) ? input[0] : input;
        if (field && field.focus) field.focus();
      });
    },
    /** Only the fields the admin has entered; the rest fall back to the saved config. */
    buildTestPayload() {
      const payload = {};
      Object.entries(GROUP_SECRET_FIELDS.wir).forEach(([key, field]) => {
        const value = (this.values[key] || "").trim();
        if (!this.locked[key] && value) {
          payload[`wir${field.charAt(0).toUpperCase()}${field.slice(1)}`] = value;
        }
      });
      const dataVaultKey = (this.values.dataVaultSubscriptionKey || "").trim();
      if (!this.locked.dataVaultSubscriptionKey && dataVaultKey) {
        payload.dataVaultSubscriptionKey = dataVaultKey;
      }
      // One input feeds both sides, since WIR and Data Vault share a tenant id here.
      if (this.tenantIdChanged) {
        payload.wirTenantId = this.tenantId;
        payload.dataVaultTenantId = this.tenantId;
      }
      return payload;
    },
    async handleTestConnection() {
      if (!this.client?.clientId) {
        this.$store.dispatch("showError", { message: "Please select a client first." });
        return;
      }

      try {
        this.testing = true;
        const response = await UserServiceV3.testTaxConfigConnection(
          this.buildTestPayload(),
          this.client.clientId
        );
        this.testResult = response?.result ?? null;
      } catch (error) {
        console.error("Failed to test tax config connection", error);
        this.$store.dispatch("showError", {
          message: "Failed to test the connection. Please try again later.",
        });
      } finally {
        this.testing = false;
      }
    },
    async handleSave() {
      if (!this.client?.clientId) {
        this.$store.dispatch("showError", { message: "Please select a client first." });
        return;
      }

      try {
        this.saving = true;

        if (this.dirtyGroups.length) await this.saveCredentials();
        if (this.toggleChanged) await this.saveTaxInfoEnabled();

        this.$emit("getClient", { clientId: this.client.clientId });
        this.$store.dispatch("showSuccess", { message: "Changes saved successfully." });
      } catch (error) {
        console.error("Failed to save tax config", error);
        this.$store.dispatch("showError", {
          message: "Failed to save tax config. Please try again later.",
        });
      } finally {
        this.saving = false;
      }
    },
    async saveCredentials() {
      const clientId = this.client.clientId;
      const groups = this.dirtyGroups;

      for (const group of groups) {
        if (this.isGroupConfigured(group)) {
          await this.patchGroup(group, clientId);
        } else {
          await this.createGroup(group, clientId);
        }
      }

      groups.forEach((group) =>
        Object.keys(GROUP_SECRET_FIELDS[group]).forEach((key) => {
          this.$set(this.values, key, "");
          this.$set(this.locked, key, true);
        })
      );
      this.savedTenantId = this.effectiveTenantId;
      this.$set(this.values, "tenantId", this.savedTenantId);
      this.$set(this.locked, "tenantId", true);
    },
    async patchGroup(group, clientId) {
      const patch = this.groupPatch(group);
      if (group === "wir") await UserServiceV3.updateWirConfig(patch, clientId);
      else await UserServiceV3.updateDataVaultConfig(patch, clientId);
    },
    async createGroup(group, clientId) {
      const tenantId = this.effectiveTenantId;
      if (group === "wir") {
        await UserServiceV3.createWirConfig(
          {
            clientSecret: this.values.wirClientSecret.trim(),
            subscriptionKey: this.values.wirSubscriptionKey.trim(),
            tenantId,
          },
          clientId
        );
        this.wirConfigured = true;
        return;
      }
      await UserServiceV3.createDataVaultConfig(
        { subscriptionKey: this.values.dataVaultSubscriptionKey.trim(), tenantId },
        clientId
      );
      this.dataVaultConfigured = true;
    },
    async saveTaxInfoEnabled() {
      await this.clientService.updateClientV3(this.client.clientId, {
        taxInfoEnabled: this.enableTaxInfo,
      });
      this.savedEnableTaxInfo = this.enableTaxInfo;
    },
    confirm({ title, message, confirmButtonText = "Confirm", cancelButtonText = "Cancel" }) {
      this.confirmDialog = {
        show: true,
        title,
        message,
        confirmButtonText,
        cancelButtonText,
      };
      return new Promise((resolve) => {
        this.confirmDialogResolve = resolve;
      });
    },
    onConfirmDialogConfirm() {
      if (this.confirmDialogResolve) {
        this.confirmDialogResolve(true);
        this.confirmDialogResolve = null;
      }
    },
    onConfirmDialogCancel() {
      if (this.confirmDialogResolve) {
        this.confirmDialogResolve(false);
        this.confirmDialogResolve = null;
      }
    },
    /** Public — called by DeveloperSettings to gate navigation away from this tab. */
    async confirmCanLeave() {
      if (!this.hasUnsavedChanges) return true;
      return this.confirm({
        title: "Unsaved tax config changes",
        message: "Your tax config changes haven't been saved yet. Leave anyway?",
        confirmButtonText: "Exit",
        cancelButtonText: "Keep editing",
      });
    },
  },
};
</script>

<style scoped>
.tax-config-toggle {
  flex: 0 0 auto;
}

/* Sits beside the Test connection button, so it hugs its text instead of filling the row
   and loses the taller padding Alert uses for full-width messages. */
.tax-config-test-all {
  flex: 0 1 auto;
  padding-top: 0.25rem !important;
  padding-bottom: 0.25rem !important;
}
</style>
